import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, Flag, Check, X, Clock, Ban, ShoppingBag, MessageSquareText, UserX, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import ModReasonDialog from "./ModReasonDialog";
import ModReasonNote from "./ModReasonNote";

const REASON_LABEL = {
  spam: "Spam / scam",
  abuse: "Harassment",
  copyright: "Copyright",
  misleading: "Misleading",
  nsfw: "Sexual / graphic",
  other: "Other",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const accountAge = (iso) => {
  if (!iso) return "unknown";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 30) return `${days}d old`;
  if (days < 365) return `${Math.floor(days / 30)}mo old`;
  return `${Math.floor(days / 365)}y old`;
};

/**
 * Troll-check dossier for whoever filed a report: account age, lifetime
 * accuracy (confirmed vs dismissed), plus their live queue and resolved
 * history so the admin can tell a reliable flagger from a serial reporter.
 */
export default function ReporterDetailDialog({ reporterId, open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [askReason, setAskReason] = useState(false);

  const load = useCallback(() => {
    if (!reporterId) return Promise.resolve();
    setLoading(true);
    setData(null);
    return api.get(`/admin/reporters/${reporterId}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Couldn't load reporter");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [reporterId, onOpenChange]);

  useEffect(() => {
    if (!open || !reporterId) return;
    load();
  }, [open, reporterId, load]);

  const banToggle = async (reason) => {
    if (!data) return;
    const banned = data.report_banned;
    setBusy(true);
    try {
      await api.post(
        `/admin/reporters/${reporterId}/${banned ? "unban" : "ban"}`,
        banned ? undefined : { reason: reason || "" },
      );
      toast.success(banned ? "Reporting ban lifted" : "Reporter banned from reporting");
      setAskReason(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const saveReason = async (reason) => {
    try {
      await api.patch(`/admin/reporters/${reporterId}/ban-reason`, { reason });
      setData((prev) => (prev ? { ...prev, report_ban_reason: reason } : prev));
      toast.success("Reason saved");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save reason");
    }
  };

  const u = data?.user;
  const stats = data?.stats || {};
  const history = data?.history || [];
  const openReports = data?.open_reports || [];
  const accuracy = stats.accuracy_pct;
  const trusted = accuracy != null && accuracy >= 60 && stats.resolved >= 2;
  const banned = data?.report_banned;
  const eligibleBan = data?.eligible_for_report_ban;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="reporter-detail-dialog"
        className="bg-[#0A0A0A] border-[#27272A] text-[#F3F4F6] sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Flag className="h-4 w-4 text-[#EF4444]" /> Reporter check
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF] text-xs">
            Is this a reliable flagger or a serial reporter?
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#6B7280] text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading reporter…
            </div>
          ) : !data ? null : (
            <div className="space-y-4">
              {/* Identity */}
              <div className="flex items-center gap-3">
                {u?.picture ? (
                  <img src={u.picture} alt="" className="h-12 w-12 rounded-full object-cover border border-[#27272A]" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white text-base font-semibold flex items-center justify-center">
                    {(u?.name || u?.email || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-[#F3F4F6] truncate" data-testid="reporter-detail-name">
                    {u?.name || "Unknown user"}
                  </div>
                  <div className="text-xs text-[#9CA3AF] truncate" data-testid="reporter-detail-email">{u?.email}</div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-[#6B7280]">
                    <Clock className="h-3 w-3" /> Joined {fmtDate(data.joined_at)} · {accountAge(data.joined_at)}
                    {u?.suspended && (
                      <span className="inline-flex items-center gap-1 text-[#F87171]">
                        <Ban className="h-3 w-3" /> suspended
                      </span>
                    )}
                    {banned && (
                      <span className="inline-flex items-center gap-1 text-[#F87171]" data-testid="reporter-banned-badge">
                        <ShieldOff className="h-3 w-3" /> reporting banned
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Ban note — jot down why, editable later */}
              {banned && (
                <div className="rounded-xl bg-[#121212] border border-[#EF4444]/30 px-3 pt-2 pb-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Ban reason</div>
                  <ModReasonNote
                    reason={data.report_ban_reason}
                    onSave={saveReason}
                    testId="reporter-ban-reason"
                  />
                </div>
              )}

              {/* Trust verdict */}
              {stats.resolved > 0 && (
                <div
                  data-testid="reporter-trust-verdict"
                  className={`rounded-xl border px-3 py-2.5 flex items-center gap-2.5 ${
                    trusted
                      ? "bg-[#10B981]/10 border-[#10B981]/40 text-[#34D399]"
                      : accuracy != null && accuracy < 30
                        ? "bg-[#EF4444]/10 border-[#EF4444]/40 text-[#F87171]"
                        : "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#FBBF24]"
                  }`}
                >
                  <ShieldCheck className="h-5 w-5 shrink-0" />
                  <div className="text-sm">
                    <span className="font-semibold">{accuracy}% accurate</span>
                    <span className="text-[#9CA3AF]"> — {stats.confirmed} of {stats.resolved} confirmed by you</span>
                  </div>
                </div>
              )}

              {/* Stat tiles */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Confirmed" value={stats.confirmed} icon={<Check className="h-3 w-3 text-[#10B981]" />} data-testid="reporter-stat-confirmed" />
                <Stat label="Dismissed" value={stats.dismissed} icon={<X className="h-3 w-3 text-[#EF4444]" />} data-testid="reporter-stat-dismissed" />
                <Stat label="Open now" value={stats.open} icon={<Flag className="h-3 w-3 text-[#F59E0B]" />} data-testid="reporter-stat-open" />
              </div>

              {/* Open reports */}
              {openReports.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5">In the queue now</div>
                  <div className="space-y-1.5">
                    {openReports.map((r) => (
                      <HistoryRow key={r.report_id} row={r} badge="open" />
                    ))}
                  </div>
                </div>
              )}

              {/* Resolved history */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5">Past reports</div>
                {history.length === 0 ? (
                  <div className="text-xs text-[#6B7280] py-3 text-center rounded-xl bg-[#121212] border border-dashed border-[#27272A]">
                    No resolved reports yet.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {history.map((h) => (
                      <HistoryRow key={h.outcome_id} row={h} badge={h.outcome} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!loading && data && (
          <DialogFooter className="shrink-0 border-t border-[#1E1E1E] pt-3 gap-2 sm:justify-between items-center">
            <span className="text-[11px] text-[#6B7280]">
              {banned
                ? "This user can't file reports."
                : eligibleBan
                  ? `${stats.dismissed} dismissed — eligible to ban`
                  : `Ban unlocks at ${data.report_ban_threshold || 3} dismissed reports`}
            </span>
            {banned ? (
              <Button
                onClick={() => banToggle()}
                disabled={busy}
                data-testid="reporter-unban-btn"
                variant="ghost"
                className="h-9 rounded-full text-[#34D399] hover:text-[#34D399] hover:bg-[#10B981]/10 text-xs"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                Lift reporting ban
              </Button>
            ) : (
              <Button
                onClick={() => setAskReason(true)}
                disabled={busy || !eligibleBan}
                data-testid="reporter-ban-btn"
                className="h-9 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <UserX className="h-3.5 w-3.5 mr-1" />}
                Ban from reporting
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
      <ModReasonDialog
        open={askReason}
        onOpenChange={setAskReason}
        title="Ban from reporting"
        description={`${u?.name || u?.email || "This user"} won't be able to flag content until you unban them, and their open reports get cleared. They can still use NoteLosh normally.`}
        confirmLabel="Ban from reporting"
        busy={busy}
        onConfirm={banToggle}
      />
    </>
  );
}

function Stat({ label, value, icon, ...rest }) {
  return (
    <div className="rounded-xl bg-[#121212] border border-[#1E1E1E] py-2.5" {...rest}>
      <div className="text-xl font-bold text-[#F3F4F6]">{value ?? 0}</div>
      <div className="text-[10px] text-[#9CA3AF] flex items-center justify-center gap-1 mt-0.5">{icon} {label}</div>
    </div>
  );
}

function HistoryRow({ row, badge }) {
  const isComment = row.target_type === "comment";
  const badgeStyle = {
    confirmed: "bg-[#10B981]/20 text-[#34D399] border-[#10B981]/40",
    dismissed: "bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/40",
    open: "bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/40",
  }[badge] || "bg-[#1E1E1E] text-[#9CA3AF] border-[#27272A]";
  const badgeLabel = { confirmed: "Confirmed", dismissed: "Dismissed", open: "Open" }[badge] || badge;
  return (
    <div className="rounded-xl bg-[#121212] border border-[#1E1E1E] px-3 py-2 flex items-center gap-2.5">
      {isComment ? <MessageSquareText className="h-3.5 w-3.5 text-[#6B7280] shrink-0" /> : <ShoppingBag className="h-3.5 w-3.5 text-[#6B7280] shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#F3F4F6] truncate">{row.content_snippet || "(no preview)"}</div>
        <div className="text-[11px] text-[#9CA3AF]">{REASON_LABEL[row.reason] || row.reason} · {fmtDate(row.resolved_at || row.created_at)}</div>
      </div>
      <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${badgeStyle}`}>{badgeLabel}</span>
    </div>
  );
}
